import React, { useState } from 'react';
import useCollections from '../hooks/useCollections';
import useTargetLocales from '../hooks/useTargetLocales';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../components/ui/table';
import { ChevronRight, ExternalLink } from 'lucide-react';

// Build a Shopify Admin URL for a Collection GID. Mirrors the pattern used
// in ProductDetail.jsx (single-tenant store: lamptwist-live).
function shopifyAdminUrl(resourceId) {
  const id = resourceId ? resourceId.split('/').pop() : '';
  return `https://admin.shopify.com/store/lamptwist-live/collections/${id}`;
}
import SyncModal from '../components/SyncModal';
import { localeLabel } from '../utils/localeFlag';

const FIELD_KEY_OPTIONS = [
  { id: '', label: 'All fields' },
  { id: 'title', label: 'Title' },
  { id: 'body_html', label: 'Body HTML' },
  { id: 'handle', label: 'Handle' },
  { id: 'meta_title', label: 'Meta title' },
  { id: 'meta_description', label: 'Meta description' },
];

const POLICY_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'translate', label: 'Translate' },
  { id: 'skip', label: 'Skip (user)' },
  { id: 'auto-skipped', label: 'Auto-skipped' },
];

const STATUS_FILTERS = [
  { id: 'all', label: 'All collections' },
  { id: 'missing', label: 'Missing translations only' },
];

// Group key = leading title segment (before the first ' > ').
// Falls back to the title itself for standalone collections like "Indoor Lighting".
function groupKeyForTitle(title) {
  if (!title) return '—';
  const idx = title.indexOf(' > ');
  return idx === -1 ? title : title.slice(0, idx);
}

// Per-row status badge (used in the expanded per-field view)
function StatusBadge({ status }) {
  if (!status) return <span className="text-muted-foreground">—</span>;
  const map = {
    pending: { className: 'bg-gray-100 text-gray-800 hover:bg-gray-100', label: 'Pending' },
    translated: { className: 'bg-blue-100 text-blue-800 hover:bg-blue-100', label: 'Translated' },
    pushed: { className: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100', label: 'Pushed' },
    failed: { className: 'bg-red-100 text-red-800 hover:bg-red-100', label: 'Failed' },
  };
  const cfg = map[status] || { className: '', label: status };
  return <Badge className={cfg.className}>{cfg.label}</Badge>;
}

// A row "needs translation" iff there's something to translate AND its
// effective policy resolves to translate. Empty source is the first gate:
// the sync's preserve-user-override CASE can leave a row marked
// translation_policy='translate' after the merchant clears its value in
// Shopify, so policy alone isn't enough to know whether work is pending.
// Same policy precedence as effectivePolicyValue: field default → row
// override → auto_skip_reason → translate-by-default.
function needsTranslation(row) {
  if (!row.raw_value) return false;
  const effective = row.effective_policy;
  if (effective === 'skip') return false;
  if (effective === 'translate') return true;
  if (row.auto_skip_reason) return false;
  return true;
}

// Roll up a collection's fields into a per-locale status for the parent row.
function rollupForLocale(fields, locale) {
  const statusKey = `${locale}_status`;
  let needed = 0, pushed = 0, failed = 0;
  for (const f of fields) {
    if (!needsTranslation(f)) continue;
    needed++;
    if (f[statusKey] === 'pushed') pushed++;
    else if (f[statusKey] === 'failed') failed++;
  }
  return { needed, pushed, failed };
}

function RollupBadge({ rollup }) {
  const { needed, pushed, failed } = rollup;
  if (needed === 0) {
    return <span className="text-muted-foreground text-xs">—</span>;
  }
  if (failed > 0) {
    return <Badge className="bg-red-100 text-red-800 hover:bg-red-100">{pushed}/{needed} · {failed} failed</Badge>;
  }
  if (pushed === needed) {
    return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">{pushed}/{needed}</Badge>;
  }
  return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">{pushed}/{needed}</Badge>;
}

function truncate(value, n = 80) {
  if (!value) return '';
  return value.length > n ? value.slice(0, n) + '…' : value;
}

// Resolve the effective policy a row will translate under. Precedence is
// field default → row override → auto_skip_reason → 'auto'. Explicit operator
// choice (field default or row override) wins over auto-classification, so a
// row with both auto_skip_reason='url' AND field_default_policy='skip'
// displays the Settings value rather than the misleading "auto".
function effectivePolicyValue(row) {
  if (row.effective_policy) return row.effective_policy;
  if (row.auto_skip_reason) return 'auto';
  return 'auto';
}

function PolicyBadge({ row }) {
  const value = effectivePolicyValue(row);
  const cls = value === 'translate'
    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300'
    : 'bg-muted text-muted-foreground';
  return (
    <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded capitalize ${cls}`}>
      {value}
    </span>
  );
}

function FieldDefaultHint({ row }) {
  if (!row.field_default_policy) return null;
  return (
    <span className="block text-[10px] text-muted-foreground mt-1">
      from default
    </span>
  );
}

export default function Collections() {
  const {
    rows,
    total,
    loading,
    error,
    fieldKey,
    setFieldKey,
    policy,
    setPolicy,
    hasHtml,
    setHasHtml,
    status,
    setStatus,
    q,
    setQ,
    page,
    setPage,
    pageSize,
    syncModalOpen,
    setSyncModalOpen,
    lastSynced,
    fetchData,
  } = useCollections();

  const { locales } = useTargetLocales();

  const [expanded, setExpanded] = useState(() => new Set());
  const [expandedPrefixes, setExpandedPrefixes] = useState(() => new Set());

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const toggleExpanded = (resourceId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(resourceId)) next.delete(resourceId);
      else next.add(resourceId);
      return next;
    });
  };

  const togglePrefix = (prefix) => {
    setExpandedPrefixes((prev) => {
      const next = new Set(prev);
      if (next.has(prefix)) next.delete(prefix);
      else next.add(prefix);
      return next;
    });
  };

  const handleSyncClose = () => {
    setSyncModalOpen(false);
    fetchData();
  };

  // Group rows by resource_id, preserving server-side order
  const groupOrder = [];
  const groups = {};
  rows.forEach((r) => {
    if (!groups[r.resource_id]) {
      groups[r.resource_id] = { title: r.title, handle: r.handle, fields: [] };
      groupOrder.push(r.resource_id);
    }
    groups[r.resource_id].fields.push(r);
  });

  // Compute first-segment prefix groups for visual headers + aggregate rollups.
  // Order preserved because the server returns rows pre-sorted by
  // split_part(title, ' > ', 1).
  const prefixOrder = [];
  const prefixCounts = {};
  const prefixMembers = {};   // prefix → [resourceId, ...]
  const prefixRollups = {};   // prefix → { de: {...}, es: {...}, ... }
  groupOrder.forEach((resourceId) => {
    const key = groupKeyForTitle(groups[resourceId].title);
    if (!(key in prefixCounts)) {
      prefixCounts[key] = 0;
      prefixMembers[key] = [];
      prefixRollups[key] = {};
      for (const loc of locales) {
        prefixRollups[key][loc] = { needed: 0, pushed: 0, failed: 0 };
      }
      prefixOrder.push(key);
    }
    prefixCounts[key] += 1;
    prefixMembers[key].push(resourceId);
    for (const loc of locales) {
      const r = rollupForLocale(groups[resourceId].fields, loc);
      prefixRollups[key][loc].needed += r.needed;
      prefixRollups[key][loc].pushed += r.pushed;
      prefixRollups[key][loc].failed += r.failed;
    }
  });

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-2xl font-bold text-foreground">Collections</h1>
        <Button onClick={() => setSyncModalOpen(true)}>Sync from Shopify</Button>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        {lastSynced ? `Last synced: ${new Date(lastSynced).toLocaleString()}` : 'Never synced'}
        {!loading && ` · ${groupOrder.length.toLocaleString()} collections · ${prefixOrder.length.toLocaleString()} prefix group(s)`}
        {!loading && status === 'missing' && ' · filtered to collections with at least one pending/failed translation'}
      </p>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select
          value={fieldKey}
          onChange={(e) => setFieldKey(e.target.value)}
          className="border border-input bg-background h-9 rounded-md px-2 text-sm"
        >
          {FIELD_KEY_OPTIONS.map((o) => (
            <option key={o.id} value={o.id}>{o.label}</option>
          ))}
        </select>

        <div className="flex gap-1">
          {STATUS_FILTERS.map((f) => (
            <Button
              key={f.id}
              variant={status === f.id ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setStatus(f.id)}
            >
              {f.label}
            </Button>
          ))}
        </div>

        <div className="flex gap-1">
          {POLICY_FILTERS.map((f) => (
            <Button
              key={f.id}
              variant={policy === f.id ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setPolicy(f.id)}
            >
              {f.label}
            </Button>
          ))}
        </div>

        <select
          value={hasHtml}
          onChange={(e) => setHasHtml(e.target.value)}
          className="border border-input bg-background h-9 rounded-md px-2 text-sm"
        >
          <option value="">All types</option>
          <option value="true">HTML only</option>
          <option value="false">Plain text only</option>
        </select>

        <Input
          type="text"
          placeholder="Search title, handle or value..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="flex-1 min-w-[200px]"
        />
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 text-red-800 p-3 mb-4 text-sm">
          {error}
        </div>
      )}

      {loading && <p className="text-sm text-muted-foreground">Loading...</p>}

      {!loading && rows.length === 0 && (
        <div className="border rounded p-8 text-center text-muted-foreground">
          No collection rows match the current filters. Try a sync if the table is empty.
        </div>
      )}

      {!loading && rows.length > 0 && (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Collection</TableHead>
                {locales.map((loc) => (
                  <TableHead key={loc} className="w-28">{localeLabel(loc)}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {prefixOrder.map((prefix) => {
                const prefixOpen = expandedPrefixes.has(prefix);
                return (
                  <React.Fragment key={prefix}>
                    <TableRow
                      className="bg-muted hover:bg-muted/80 cursor-pointer"
                      onClick={() => togglePrefix(prefix)}
                    >
                      <TableCell className="py-2">
                        <div className="flex items-center gap-2">
                          <ChevronRight
                            className={`h-4 w-4 transition-transform ${prefixOpen ? 'rotate-90' : ''}`}
                          />
                          <span className="text-xs font-semibold uppercase tracking-wider text-foreground">
                            {prefix}
                          </span>
                          <span className="text-xs text-muted-foreground font-normal">
                            · {prefixCounts[prefix]} {prefixCounts[prefix] === 1 ? 'collection' : 'collections'}
                          </span>
                        </div>
                      </TableCell>
                      {locales.map((loc) => (
                        <TableCell key={loc}>
                          <RollupBadge rollup={prefixRollups[prefix][loc]} />
                        </TableCell>
                      ))}
                    </TableRow>
                    {prefixOpen && prefixMembers[prefix].map((resourceId) => {
                      const g = groups[resourceId];
                      const isOpen = expanded.has(resourceId);
                      const localeRollups = Object.fromEntries(
                        locales.map((loc) => [loc, rollupForLocale(g.fields, loc)]),
                      );
                      // Hide the prefix in the row label when the header already names it
                      // so e.g. /andtradition renders as "&Tradition" (leaf) under the
                      // "Brands" header rather than "Brands > &Tradition".
                      const leafLabel = g.title && g.title.includes(' > ')
                        ? g.title.slice(g.title.indexOf(' > ') + 3)
                        : g.title;
                      return (
                        <React.Fragment key={resourceId}>
                          <TableRow
                            className="cursor-pointer hover:bg-accent"
                            onClick={() => toggleExpanded(resourceId)}
                          >
                            <TableCell>
                              <div className="flex items-center gap-2 pl-6">
                                <ChevronRight
                                  className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                                />
                                <span className="font-medium">{leafLabel || resourceId}</span>
                                <span className="text-xs text-muted-foreground">/{g.handle}</span>
                                <a
                                  href={shopifyAdminUrl(resourceId)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  title="Open in Shopify Admin"
                                  className="text-muted-foreground hover:text-foreground inline-flex items-center"
                                >
                                  <ExternalLink className="h-3.5 w-3.5" />
                                </a>
                              </div>
                            </TableCell>
                            {locales.map((loc) => (
                              <TableCell key={loc}>
                                <RollupBadge rollup={localeRollups[loc]} />
                              </TableCell>
                            ))}
                          </TableRow>
                    {isOpen && (
                      <TableRow className="bg-muted/30">
                        <TableCell colSpan={locales.length + 1} className="p-0">
                          <table className="w-full">
                            <thead>
                              <tr className="text-xs text-muted-foreground border-b">
                                <th className="text-left font-normal pl-10 py-2 w-40">Field</th>
                                <th className="text-left font-normal py-2">Value</th>
                                <th className="text-left font-normal py-2 w-32">Policy</th>
                                {locales.map((loc) => (
                                  <th key={loc} className="text-left font-normal py-2 w-28">{localeLabel(loc)}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {g.fields.map((row) => (
                                <tr key={row.id} className="border-b last:border-b-0">
                                  <td className="pl-10 py-2 text-xs font-mono">{row.field_key}</td>
                                  <td className="py-2 text-sm max-w-md truncate pr-4" title={row.raw_value || ''}>
                                    {truncate(row.raw_value, 80)}
                                  </td>
                                  <td className="py-2">
                                    <div className="flex items-center gap-2">
                                      <div>
                                        <PolicyBadge row={row} />
                                        <FieldDefaultHint row={row} />
                                      </div>
                                      {row.field_key === 'handle' && effectivePolicyValue(row) === 'translate' && (
                                        <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
                                          URL slug
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  {locales.map((loc) => (
                                    <td key={loc} className="py-2"><StatusBadge status={row[`${loc}_status`]} /></td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </TableCell>
                      </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-muted-foreground">
              Page {page} of {totalPages} · {total.toLocaleString()} total rows
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}

      <SyncModal
        open={syncModalOpen}
        onClose={handleSyncClose}
        endpoint="/api/transly/collections/sync/"
        statusEndpoint="/api/transly/collections/sync-status/"
        title="Syncing Collections"
      />
    </div>
  );
}
